import { useState, useCallback } from 'react'
import { AutosuggestPropsMultiSection, RenderSuggestionsContainerParams } from 'react-autosuggest'
import { AsyncAutosuggest } from 'builder/components/AsyncAutosuggest'
import {
  DefaultSectionType,
  RenderSuggestionComponentStateType,
} from 'builder/components/AsyncAutosuggest/types'
import { Icon20 } from 'builder/components/Icon'

import {
  Container,
  Input,
  InputPlaceholder,
  IconWrapper,
  Menu,
  SectionHeader,
  Item,
  RejectIconWrapper,
  InputContainer,
  InputWrapper,
  Separator,
  MenuInputPlaceholderPopup,
  HoverCustomItemContainer,
} from './styles'
import { SearchSuggestionType, AutoSuggestFieldProps } from './types'

const renderSectionTitle = (section: DefaultSectionType) =>
  section.title && <SectionHeader>{section.title}</SectionHeader>

const AutoSuggestField = ({
  placeholder,
  disabled,
  icon,
  renderIconMenu,
  defaultSuggestions,
  clearPreviousSuggestions,
  value,
  onChange,
  onClear,
  onClick,
  onBlur,
  onSuggestionSelected,
  readOnly,
  overwritePopupWidth,
  withHover = true,
  renderCustomItemSuggestion,
  ...rest
}: AutoSuggestFieldProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const renderDefaultSuggestion = (
    suggestion: SearchSuggestionType,
    state: RenderSuggestionComponentStateType,
  ) => {
    if (!suggestion) {
      return null
    }

    return (
      <Item $isHighlighted={state.isHighlighted}>
        <div>
          {renderIconMenu ? renderIconMenu(suggestion) : icon}
          {suggestion.formatted ?? suggestion.text}
        </div>
        <HoverCustomItemContainer>
          {renderCustomItemSuggestion && renderCustomItemSuggestion(suggestion)}
        </HoverCustomItemContainer>
      </Item>
    )
  }
  const hasCloseIcon = typeof value === 'string' && value.length > 0 && onClear

  const handleChange = useCallback(
    (event: React.FormEvent<HTMLElement>) => {
      const target = event.target as HTMLInputElement
      if (target.value !== undefined) {
        onChange(target.value)
      }
    },
    [onChange],
  )

  const renderDefaultSuggestionsContainer = ({
    containerProps: { key, ...containerProps },
    children,
  }: RenderSuggestionsContainerParams) => {
    if (!isExpanded) {
      children && setIsExpanded(true)
      return null
    }

    return (
      <Menu {...containerProps} key={key} $overwritePopupWidth={overwritePopupWidth}>
        <MenuInputPlaceholderPopup />
        {children && (
          <>
            <Separator />
            {children}
          </>
        )}
      </Menu>
    )
  }

  return (
    <Container onClick={onClick} withHover={withHover}>
      <AsyncAutosuggest<
        SearchSuggestionType,
        AutosuggestPropsMultiSection<SearchSuggestionType, DefaultSectionType>
      >
        {...rest}
        value={value}
        onChange={e => onChange(e.target.value)}
        multiSection={value.length === 0}
        renderSuggestionsContainer={renderDefaultSuggestionsContainer}
        renderSectionTitle={renderSectionTitle}
        renderSuggestion={renderDefaultSuggestion}
        getSectionSuggestions={section => section && section.suggestions}
        shouldRenderSuggestions={() => true}
        defaultSuggestions={defaultSuggestions}
        onSuggestionSelected={(e, selected) => {
          setIsExpanded(false)
          onSuggestionSelected(selected.suggestion)
        }}
        inputProps={{
          placeholder,
          value,
          onBlur: () => {
            setIsExpanded(false)
            onBlur && onBlur()
          },
          disabled,
          onChange: handleChange,
          onClick: () => setIsExpanded(true),
          onKeyDown: event => {
            if (event.key === 'Enter' || event.key === 'Escape') {
              setIsExpanded(false)
            }
          },
          readOnly,
        }}
        clearPreviousSuggestions={clearPreviousSuggestions}
      >
        {({ key, ...inputProps }) => (
          <InputPlaceholder $isExpanded={isExpanded} $overwritePopupWidth={overwritePopupWidth}>
            <InputContainer $isExpanded={isExpanded}>
              <InputWrapper>
                <IconWrapper>{icon}</IconWrapper>
                <Input {...inputProps} key={key} />
                {hasCloseIcon && (
                  <RejectIconWrapper>
                    <Icon20.RejectIcon onClick={onClear} />
                  </RejectIconWrapper>
                )}
              </InputWrapper>
            </InputContainer>
          </InputPlaceholder>
        )}
      </AsyncAutosuggest>
    </Container>
  )
}

export default AutoSuggestField
